import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { SharedService } from '../../..';
import { Answer, Ethnicity, Guest, GuestType, KidExpanded, Question, QuestionDataType, QuestionTemplateCode } from '../../../../../../common/interfaces';
import { FieldValidator } from '../../../common/validator';
import { questionColumnClasses } from '../../../common/question-layout';

@Component({
  selector: 'guest-form',
  styleUrls: ['./guest-form.component.scss'],
  templateUrl: './guest-form.component.pug'
})
export class GuestForm implements OnInit {
  public columnClasses = questionColumnClasses;
  @Input() public guestNumber: number;
  @Input() public guestLabel: string;
  @Input() public isFamilyMode: boolean;
  @Input() public isChildren: boolean;
  @Input() public guest: Guest;
  @Input() public child: KidExpanded;
  @Input() public questions: Array<Question>;
  @Input() public isEditing = false;
  @Input() public countryId: number; // THIS IS NOT SET IN THE PARENT COMPONENT
  @Input() public showHighlightedError: boolean;
  @Output() public guestValidationStateChanged = new EventEmitter<boolean>();
  @Output() public onAskIfChangedParent: EventEmitter<any> = new EventEmitter();

  public form: FormGroup;
  public guests: Array<any>;
  public options: Array<any>;
  public guestQuestions: Array<Question>;
  public guestAnswers: any = {};
  private isValid: boolean;
  public formControlSuffix: string;
  public guestType: string = '';
  // kids: KidExpanded[];

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private sharedService: SharedService
  ) { }

  public ngOnInit() {
    this.guestQuestions = this.questions?.map(x => structuredClone(x));
    this.form = this.formBuilder.group({});
    let guestType = this.isFamilyMode ? 'Child ' : 'Guest ';
    if (this.isChildren) {
      guestType = 'Child ';
    }
    this.guestType = guestType;
    this.guestLabel = guestType + this.guestNumber + ' ';
    if (!this.guest) { this.guest = {}; }
    this.formControlSuffix = `${this.guestNumber}${this.isFamilyMode}`;
    // this.kids = this.sharedService.getKids()
    this.setUpForm();
  }

  public valid() {
    return this.isValid;
  }

  public values() {
    let guest: any = this.guest;
    guest.Answers = guest.Answer ? guest.Answer : [];
    // if(this.isFamilyMode) {
    let type: GuestType = this.isFamilyMode ? 'Child' : 'Guest';
    guest.type = type;
    // }
    if (this.guestQuestions) {
      this.guestQuestions?.forEach(question => {
        let control = this.form.controls[question.formControlName];
        let existing = this.guestAnswers[question.id];

        const ansMap = question.configJSON.answerMap || question.configJSON.answers || []
        const answerMap = (question.configJSON.answers || []).reduce((p, c, i) => (p[c] = ansMap[i], p), {})
        const optMap = question.configJSON.optionMap || question.configJSON.options || []
        const optionMap = (question.configJSON.options || []).reduce((p, c, i) => (p[c] = optMap[i], p), {})

        if (control.value || existing) {
          // get answer data
          let existingIdx = guest.Answers.findIndex(ans => existing && ans.id === existing.id)
          let values = control.value ? [] : existing.values || [];
          let mappedValues = control.value ? [] : existing.mappedValues || [];
          let checkgroup = question.configJSON && question.configJSON.dataType === QuestionDataType.CheckGroup;
          if (control.value) {
            if (checkgroup) {
              // make an array of selected values
              let obj = Object.keys(control.value).reduce((p, c) => {
                if (control.value[c] === true) p[c] = true;
                return p;
              }, {});
              values = Object.entries(obj);
              mappedValues = values.map(([k, v]: [string, string]) => ([answerMap[k], v]));
            } else {
              if (question.configJSON?.dataType === QuestionDataType.Grid) {
                mappedValues = [Object.fromEntries(Object.entries(control.value).map(([k, v]: [string, string]) => ([optionMap[k], answerMap[v]])))];
              } else {
                mappedValues.push(answerMap[control.value]);
              }
              values.push(control.value);
            }
          }
          if (existingIdx !== -1) {
            let answer: Answer = {
              ...guest.Answer[existingIdx],
              questionId: question.id,
              questionTemplateId: question.questionTemplateId,
              answerJSON: { values, mappedValues, label: question.label }
            }
            guest.Answer[existingIdx] = answer
          } else {
            let answer: Answer = {
              questionId: question.id,
              questionTemplateId: question.questionTemplateId,
              answerJSON: { values, mappedValues, label: question.label }
            };
            guest.Answers.push(answer);
          }

          // update gender/ethnicity/age properties
          if (question.fieldCode === QuestionTemplateCode.Ethnicity || question.fieldCode === QuestionTemplateCode.ChildEthnicity) {
            const full = checkgroup ? Object.entries(control.value).filter(([x, y]) => !!x && !!y).map((([x]) => answerMap[x])) as Ethnicity[] : [answerMap[control.value]]
            guest.ethnicities = full.filter(x => !!x)
            guest.ethnicity = checkgroup ? guest.ethnicities.length > 1 ? 'Multi-Ethnic' : guest.ethnicities[0] : answerMap[control.value];
          } else if (question.fieldCode === QuestionTemplateCode.Gender || question.fieldCode === QuestionTemplateCode.ChildGender) {
            guest.gender = answerMap[control.value];
          } else if (question.fieldCode === QuestionTemplateCode.Age || question.fieldCode === QuestionTemplateCode.ChildAge) {
            guest.ageGroup = control.value;
          }
        }
      });
    }
    if (!this.isFamilyMode) {
      guest.firstName = this.form.controls[`firstName${this.formControlSuffix}`].value
      guest.lastName = this.form.controls[`lastName${this.formControlSuffix}`].value
      guest.email = this.form.controls[`email${this.formControlSuffix}`].value
      guest.phoneNumber = this.form.controls[`phoneNumber${this.formControlSuffix}`].value
      guest.phoneType = this.form.controls[`phoneType${this.formControlSuffix}`].value
    }
    return guest;
  }

  // show/hide askIf questions based on parent answer value
  public onAskIfChanged($event) {
    let parentId = $event.questionId;
    let newAnswer = $event.answer;
    let dataType = $event.dataType;
    this.guestQuestions?.forEach((linkedQuestion) => {
      const display = (linkedQuestion.configJSON.displayOptions || [])
      if (!!display.find(config => config.askIfId === parentId)) {
        let willShow = false;
        const show = display.map((config) => {
          let shouldShow;
          let answer = config.askIfId === parentId ? newAnswer : this.form.controls[config.askIfId] ? this.form.controls[config.askIfId].value : null;
          if (answer) {
            switch (config.askIfOperator) {
              case 'IS':
                if (dataType !== 1) {
                  shouldShow = answer === config.askIfValue;
                } else {
                  shouldShow = answer.toLowerCase() === config.askIfValue.toLowerCase();
                }
                break;
              case 'ISN\'T':
                if (dataType !== 1) {
                  shouldShow = answer !== config.askIfValue;
                } else {
                  shouldShow = answer.toLowerCase() !== config.askIfValue.toLowerCase();
                }
                break;
              case 'INCLUDES':
              case 'EXCLUDES':
                if (dataType !== 1) {
                  shouldShow = (config.askIfOption ? answer && answer[config.askIfOption] === config.askIfValue : answer && answer[config.askIfValue]) === !!(config.askIfOperator === 'INCLUDES');
                } else {
                  let found = answer.toLowerCase().search(config.askIfValue.toLowerCase()) > -1;
                  shouldShow = (found && config.askIfOperator === 'INCLUDES')
                    || (!found && config.askIfOperator !== 'INCLUDES');
                }
                break;
              case 'COUNT GREATER THAN':
              case 'COUNT LESS THAN':
                let values = answer.filter((value) => {
                  return value === true;
                });
                if (config.askIfOperator === 'COUNT GREATER THAN') {
                  shouldShow = values.length > config.askIfValue;
                } else {
                  shouldShow = values.length < config.askIfValue;
                }
              default:
                break;
            }
          }
          return shouldShow
        })
        if (linkedQuestion.configJSON.displayOperator === "CUSTOM") {
          const condition = linkedQuestion.configJSON.customLogic || ""
          const accumulator = {
            level: 0,
            operator: [],
            not: [],
            value: [],
            match: show
          }
          try {
            const result: any = Array.from(condition.replace(/\s/g, '')).reduce((s: any, char, i, arr) => {
              switch (char) {
                case "&":
                case "|":
                  s.operator[s.level] = char
                  break;
                case "!":
                  s.not[s.level] = true
                  break;
                case "(":
                  s.level++;
                  break;
                case ")":
                  s.level--;
                case "$":
                  let val = char === "$" ? s.match[parseInt(arr.join('').slice(i).match(/^[^\d]*(\d+)/)[1]) - 1] : s.value.pop()
                  if (s.not[s.level]) {
                    val = !val
                    s.not[s.level] = false
                  }
                  if (typeof s.value[s.level] !== 'boolean') {
                    s.value[s.level] = val
                  } else if (s.operator[s.level] === "&") {
                    s.value[s.level] = s.value[s.level] && val
                  } else if (s.operator[s.level] === "|") {
                    s.value[s.level] = s.value[s.level] || val
                  }
                  break;
              }
              return s
            }, accumulator)
            willShow = !!result.value[0]
          } catch (err) {
            console.log("ERROR PARSING CUSTOM LOGIC", err)
          }
        } else {
          willShow = show.reduce((p, c) => (linkedQuestion.configJSON.displayOperator === "ALL" ? p && c : p || c))
        }
        if (!this.form.controls[linkedQuestion.formControlName]) { return; }
        if (linkedQuestion.isRequired) {
          let validators = willShow ? linkedQuestion.configJSON.dataType === QuestionDataType.CheckGroup ? FieldValidator.requiredCheckgroup : linkedQuestion.configJSON.dataType === QuestionDataType.Grid ? FieldValidator.requiredGridQuestion : Validators.required : null;
          this.form.controls[linkedQuestion.formControlName].setValidators(validators);
        }
        linkedQuestion.hidden = !willShow;
      }
    });
    this.onAskIfChangedParent.emit($event)
  }

  private setUpForm() {
    let group: any = {};
    let guest: any = this.isFamilyMode ? this.child : this.guest;
    if (guest && Object.keys(guest).length) {
      if (Array.isArray(guest.Answer)) {
        guest.Answer.forEach((answer) => {
          if (answer.answerJSON && answer.answerJSON.values[0]) {
            let value = answer.answerJSON.values
            let mappedValues = answer.answerJSON.mappedValues
            this.guestAnswers[answer.questionId] = {
              id: answer.id,
              value: Array.isArray(value[0]) ? value.filter(x => x[1]).map(x => x[0]) : value[0],
              values: value,
              mappedValues
            }
          }
        })
      }
    }
    // this.guestQuestions = guest.Answer.map(x => x.Question);
    // }
    // } else if (this.questions && !guest) {
    this.guestQuestions?.forEach(question => {
      // set empty answer for freeform text questions to prevent 'undefined' in textarea
      let q: any = question;
      if (q.configJSON.dataType === 1) {
        q.answer = this.guestAnswers[q.id]?.value || q.answer || '';
      } else if (this.guestAnswers[q.id]) {
        if (Array.isArray(this.guestAnswers[q.id].value) && q.configJSON.dataType === 408) {
          q.answer = Object.fromEntries(this.guestAnswers[q.id].values)
        } else {
          q.answer = this.guestAnswers[q.id].value || null
        }
      }

      const formControlName = `${q.id}${this.formControlSuffix}`;
      question.formControlName = formControlName;
      if (!q.isRequired || q.hidden || this.isEditing) {
        group[formControlName] = new FormControl(q.answer || null);
      } else {
        const validator = q.configJSON.dataType === QuestionDataType.CheckGroup ? FieldValidator.requiredCheckgroup : q.configJSON.dataType === QuestionDataType.Grid ? FieldValidator.requiredGridQuestion : Validators.required
        group[formControlName] = new FormControl(q.answer || null, validator);
      }
    });
    if (!this.isFamilyMode) {
      group[`firstName${this.formControlSuffix}`] = new FormControl(this.guest.firstName || '', FieldValidator.validateName);
      group[`lastName${this.formControlSuffix}`] = new FormControl(this.guest.lastName || '', FieldValidator.validateName);
      group[`email${this.formControlSuffix}`] = new FormControl(this.guest.email, FieldValidator.emailFormat);
      group[`phoneNumber${this.formControlSuffix}`] = new FormControl(this.guest.phoneNumber, FieldValidator.validatePhoneNumber);
      group[`phoneType${this.formControlSuffix}`] = new FormControl(this.guest.phoneType || null);
    }

    this.form = this.formBuilder.group(group);
    this.form.valueChanges.subscribe(data => this.checkValidationState());
  }

  private checkValidationState() {
    if (!this.isValid) {
      this.isValid = true;
      this.guestValidationStateChanged.emit();
    }
    let valid = !this.form || this.form.valid;
    if (this.isValid === undefined || valid !== this.isValid) {
      this.isValid = valid;
      this.guestValidationStateChanged.emit();
    }
  }
}
